import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useParams } from "react-router";
import { getCustomer, getCustomerData, type CustomerData } from "@data/customers";
import type { CustomerConfig } from "@data/types";

type CustomerContextValue = {
  customer: CustomerConfig;
  data: CustomerData;
};

const CustomerContext = createContext<CustomerContextValue | null>(null);

export function useCustomer(): CustomerConfig {
  const ctx = useContext(CustomerContext);
  if (!ctx) throw new Error("useCustomer must be used within CustomerProvider");
  return ctx.customer;
}

export function useCustomerData(): CustomerData {
  const ctx = useContext(CustomerContext);
  if (!ctx) throw new Error("useCustomerData must be used within CustomerProvider");
  return ctx.data;
}

type Props = {
  children: ReactNode;
};

export function CustomerProvider({ children }: Props) {
  const { customer: customerId } = useParams<{ customer: string }>();
  const customer = useMemo(
    () => (customerId ? getCustomer(customerId) : undefined),
    [customerId],
  );
  const data = useMemo(
    () => (customerId ? getCustomerData(customerId) : undefined),
    [customerId],
  );

  useEffect(() => {
    if (!customer) return;
    const root = document.documentElement;
    root.style.setProperty("--customer-accent", customer.theme.accentColor);
    root.style.setProperty("--customer-accent-light", customer.theme.accentColorLight);
    root.style.setProperty("--customer-secondary", customer.theme.secondaryAccent);
    root.style.setProperty("--slide-accent-bar", customer.theme.slideAccentBar);
    return () => {
      root.style.removeProperty("--customer-accent");
      root.style.removeProperty("--customer-accent-light");
      root.style.removeProperty("--customer-secondary");
      root.style.removeProperty("--slide-accent-bar");
    };
  }, [customer]);

  if (!customer || !data) {
    return null;
  }

  return (
    <CustomerContext.Provider value={{ customer, data }}>
      {children}
    </CustomerContext.Provider>
  );
}
